import { isTapeReading } from './tradeHelpers';
import { computeWinLossStats } from '../statsUtils';

const toDayNumber = (year, month, day) => (Number(year) * 10000) + (Number(month) * 100) + Number(day);

const fromDayNumber = (value) => ({
  year: Math.floor(value / 10000),
  month: Math.floor(value / 100) % 100,
  day: value % 100,
});

export function isJournalDayEntry(entry) {
  return Boolean(entry) && !entry.isDeposit && !entry.isPayout && !entry.isResetExcluded;
}

export function isPnlTrade(entry) {
  return isJournalDayEntry(entry)
    && !isTapeReading(entry)
    && entry.pnl !== undefined
    && entry.pnl !== null
    && entry.pnl !== '';
}

// Sorted, de-duplicated days (as yyyymmdd numbers) that have journal entries.
export function getJournalDayNumbers(entries = []) {
  const days = new Set();
  entries.forEach((entry) => {
    if (!isJournalDayEntry(entry)) return;
    const year = Number(entry.year);
    const month = Number(entry.month);
    const day = Number(entry.day);
    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return;
    if (month < 1 || month > 12 || day < 1 || day > 31) return;
    days.add(toDayNumber(year, month, day));
  });
  return Array.from(days).sort((a, b) => a - b);
}

/**
 * Previous/next day with journal entries, or the neighbouring calendar day when there is none.
 * direction: -1 (previous) or 1 (next). Returns { year, month, day } as numbers.
 */
export function getAdjacentJournalDay(entries, current, direction) {
  const currentNumber = toDayNumber(current.year, current.month, current.day);
  const days = getJournalDayNumbers(entries || []);
  const match = direction < 0
    ? [...days].reverse().find((value) => value < currentNumber)
    : days.find((value) => value > currentNumber);
  if (match) return fromDayNumber(match);
  const fallback = new Date(Number(current.year), Number(current.month) - 1, Number(current.day) + (direction < 0 ? -1 : 1));
  return { year: fallback.getFullYear(), month: fallback.getMonth() + 1, day: fallback.getDate() };
}

// Display-only day summary: trade count, win rate (break-even threshold aware), best/worst trade P&L.
export function summarizeDayTrades(entries = [], beThreshold) {
  const trades = entries.filter(isPnlTrade);
  if (!trades.length) return null;
  const stats = computeWinLossStats(trades, beThreshold);
  const pnls = trades.map((entry) => Number(entry.pnl) || 0);
  return {
    count: trades.length,
    winRate: stats.wins + stats.losses > 0 ? stats.winRate : null,
    best: Math.max(...pnls),
    worst: Math.min(...pnls),
  };
}
